import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Grid2, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  Button, 
  Tabs, 
  Tab, 
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  alpha,
  useTheme,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import BasePage from '../../pages/BasePage';
import { useBusiness } from '../../contexts/BusinessContext';
import { useBusinessAgent } from '../../contexts/BusinessAgentContext';
import AddClientForm from './AddClientForm';
import ClientsTable from './ClientsTable';
import RecentActivityTable from './RecentActivityTable';
import { analytics } from '../../services/analytics';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useAuth } from '../../contexts/AuthContext';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HomeIcon from '@mui/icons-material/Home';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import ReplyIcon from '@mui/icons-material/Reply';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupIcon from '@mui/icons-material/Group';
import { Client } from '../../types/business';

const AgentDashboard = () => {
  const theme = useTheme();
  const { business } = useBusiness();
  const { user } = useAuth();
  const { 
    isAgent,
    agentError,
    clients,
    totalClients,
    delegationLimit,
    currentDelegations,
    messages,
    unreadMessages,
    monthlyStats,
    addClient,
    isAddingClient,
    updateClient,
    isUpdatingClient
  } = useBusinessAgent();

  const [activeTab, setActiveTab] = useState(0);
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [editClientModalOpen, setEditClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  // Log agent_dashboard_open analytics event
  useAnalytics(
    () => {
      if (user?.uuid) {
        analytics.logAgentDashboardOpen(
          user.uuid,
          business?.id || null,
          business?.name || null
        );
      }
    },
    [user?.uuid, business?.id, business?.name]
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddClientOpen = () => {
    setAddClientModalOpen(true);
  };

  const handleAddClientClose = () => {
    setAddClientModalOpen(false);
  };

  const handleEditClientOpen = (client: Client) => {
    setEditingClient(client);
    setEditClientModalOpen(true);
  };

  const handleEditClientClose = () => {
    setEditClientModalOpen(false);
    setEditingClient(null);
  };

  const handleAddClientSubmit = async (clientData: {
    clientEmail: string;
    clientName?: string;
    clientPhone?: string;
    clientDetails?: string;
  }) => {
    return await addClient(clientData);
  };

  const handleEditClientSubmit = async (
    clientEmail: string,
    updates: {
      clientName?: string;
      clientEmail?: string;
      clientPhone?: string;
      clientDetails?: string;
    }
  ) => {
    return await updateClient(clientEmail, updates);
  };

  const delegationPercentage = Math.min(100, (currentDelegations / delegationLimit) * 100);
  
  // Show error if user is not an agent
  if (!isAgent && agentError) {
    return (
      <BasePage
        title="Access Denied | CribCritic"
        description="Agent access required"
        keywords="agent dashboard, access denied"
      >
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 2,
              background: theme => `linear-gradient(120deg, ${alpha(theme.palette.error.light, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.1)} 100%)`,
              border: theme => `1px solid ${alpha(theme.palette.error.main, 0.2)}`
            }}
          >
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                bgcolor: 'error.main', 
                mx: 'auto', 
                mb: 2 
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 40 }} />
            </Avatar>
            
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Access Denied
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Agent Dashboard Access Required
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {agentError}
            </Alert>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              You need to be registered as an agent to access this dashboard. 
              Please contact your business administrator to be added as an agent.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                href="/homes"
                sx={{ borderRadius: 2 }}
              >
                Go to My Homes
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                href="/contact"
                sx={{ borderRadius: 2 }}
              >
                Contact Support
              </Button>
            </Box>
          </Paper>
        </Container>
      </BasePage>
    );
  }

  // Show loading while verifying agent status
  if (!isAgent) {
    return (
      <BasePage
        title="Agent Dashboard | CribCritic"
        description="Loading agent dashboard"
        keywords="agent dashboard, loading"
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Verifying agent access...
              </Typography>
            </Box>
          </Box>
        </Container>
      </BasePage>
    );
  }

  const statsData = [
    {
      title: "Total Clients",
      value: totalClients,
      change: `+${monthlyStats.newClients} this month`,
      icon: <PeopleAltIcon />,
      color: theme.palette.primary.main
    },
    {
      title: "Active Clients",
      value: `${currentDelegations} / ${delegationLimit}`,
      change: currentDelegations >= delegationLimit * 0.85 ? "Near delegation limit" : null,
      icon: <PersonIcon />,
      color: currentDelegations >= delegationLimit ? theme.palette.error.main : theme.palette.success.main
    },
    {
      title: "Properties Analyzed",
      value: monthlyStats.totalAnalyses,
      change: "+15% vs last month",
      icon: <HomeIcon />,
      color: theme.palette.info.main
    },
    {
      title: "Unread Messages",
      value: "—",
      change: "Coming soon",
      icon: <MessageIcon />,
      color: theme.palette.grey[400]
    }
  ];

  return (
    <BasePage
      title={`Agent Dashboard - ${business?.name || 'CribCritic'}`}
      description="Manage your clients and view your agent dashboard"
      keywords="agent dashboard, client management, real estate"
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Agent Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your clients and track property analysis progress
            </Typography>
            {business && (
              <Chip 
                label={`${business.name} Agent`} 
                color="primary" 
                size="small" 
                sx={{ mt: 1 }} 
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GroupIcon />}
              href="/business/agent/referrals"
              sx={{ borderRadius: 2 }}
            >
              Referrals
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<MessageIcon />}
              sx={{ borderRadius: 2 }}
              disabled
            >
              New Message (Coming Soon)
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          {statsData.map((card, index) => (
            <Grid2 size={{xs:12, sm:6, md:3}} key={index}>
              <Card sx={{
                borderRadius: 2,
                height: '100%',
                boxShadow: 2,
                transition: 'all 0.3s ease-in-out',
                cursor: card.title === "Unread Messages" ? 'default' : 'pointer',
                opacity: card.title === "Unread Messages" ? 0.6 : 1,
                '&:hover': card.title === "Unread Messages" ? {} : {
                  transform: 'translateY(-4px)',
                  boxShadow: theme => `0 8px 16px ${alpha(theme.palette.common.black, 0.15)}`,
                }
              }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: alpha(card.color, 0.1), color: card.color }}>
                      {card.icon}
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" fontWeight="medium" component="div">
                      {card.title}
                    </Typography>
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="h3" fontWeight="bold" component="div">
                    {card.value}
                  </Typography>
                  
                  {/* Add progress indicator for Active Clients delegation capacity */}
                  {card.title === "Active Clients" && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={delegationPercentage} 
                        color={delegationPercentage > 85 ? "warning" : "primary"}
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {delegationPercentage > 85 ? "Approaching delegation limit" : "CribCritic account delegations"}
                      </Typography>
                    </Box>
                  )}
                  
                  {card.change && card.title !== "Active Clients" && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Chip
                        label={card.change}
                        color={card.title === "Unread Messages" ? "default" : "success"}
                        size="small"
                        sx={{ mr: 1, height: 20 }}
                      />
                    </Box>
                  )}
                  
                  {card.change && card.title === "Active Clients" && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Chip 
                        label={card.change}
                        color={delegationPercentage > 85 ? "warning" : "info"}
                        size="small" 
                        sx={{ mr: 1, height: 20 }} 
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid2>
          ))}
        </Grid2>


        {/* Tab Navigation */}
        <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="agent dashboard tabs">
            <Tab label="Clients" />
            <Tab label="Recent Activity" />
            <Tab label="Messages" disabled icon={<Chip label="Coming Soon" size="small" color="default" sx={{ ml: 1 }} />} iconPosition="end" />
          </Tabs>
        </Box>

        {/* Clients Tab */}
        {activeTab === 0 && (
          <ClientsTable
            clients={clients}
            totalClients={totalClients}
            isAddingClient={isAddingClient}
            onAddClient={handleAddClientOpen}
            onEditClient={handleEditClientOpen}
          />
        )}

        {/* Recent Activity Tab */}
        {activeTab === 1 && (
          <RecentActivityTable viewType="agent" />
        )}

        {/* Messages Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Messages
                {unreadMessages > 0 && (
                  <Badge badgeContent={unreadMessages} color="error" sx={{ ml: 2 }}>
                    <MessageIcon />
                  </Badge>
                )}
              </Typography>
              <Button variant="outlined" size="small">
                Mark All Read
              </Button>
            </Box>
            
            <List>
              {messages.map((message, index) => (
                <Box key={message.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 2,
                      bgcolor: !message.isRead ? alpha(theme.palette.primary.light, 0.1) : 'transparent',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {message.clientName.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: !message.isRead ? 'bold' : 'normal' }}>
                              {message.clientName}
                            </Typography>
                            {message.isUrgent && (
                              <Chip label="Urgent" color="error" size="small" sx={{ mr: 1 }} />
                            )}
                            {!message.isRead && (
                              <Chip label="New" color="primary" size="small" />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {message.timestamp}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: !message.isRead ? 'medium' : 'normal', mb: 0.5 }}>
                            {message.subject}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {message.preview}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton size="small" color="primary">
                      <ReplyIcon />
                    </IconButton>
                  </ListItem>
                  {index < messages.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Paper>
        )}
      </Container>

      {/* Add Client Modal */}
      <AddClientForm
        open={addClientModalOpen}
        onClose={handleAddClientClose}
        onSubmit={handleAddClientSubmit}
        isSubmitting={isAddingClient}
      />

      {/* Edit Client Modal */}
      <AddClientForm
        open={editClientModalOpen}
        onClose={handleEditClientClose}
        onSubmit={handleAddClientSubmit} // Not used in edit mode
        onUpdate={handleEditClientSubmit}
        isSubmitting={isUpdatingClient}
        isEdit={true}
        editClient={editingClient || undefined}
      />
    </BasePage>
  );
};

export default AgentDashboard;